/**
 * Solo play: you against the computer, entirely in this tab.
 *
 * It runs the same `shared/engine` the droplet runs, so the rules cannot drift
 * between modes — and it runs the same `shared/bot`, so the opponent you beat
 * offline is the one that takes over your seat online when your phone dies.
 *
 * The engine mutates in place and holds no timers. Both of those are load-
 * bearing here: the mutation means a React update needs an explicit clone (the
 * `step` helper below), and the missing timers mean this component owns the
 * pacing — how long a FARKLE stamp sits, how long a bot appears to think.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { BOT_NAMES, botMove, type Difficulty } from '@shared/bot';
import {
  bank,
  clearHeld,
  endTurn,
  newGame,
  roll,
  takeCarry,
  toggle,
  viewFor,
  type GameState,
  type Rules,
} from '@shared/engine';
import { BOT_PICK_MS, BOT_THINK_MS, FARKLE_BEAT_MS } from '@shared/protocol';
import { Table } from '../game/Table';
import { Check, Eyebrow, Field, Masthead, Opt, Page, fmt } from '../ui/shell';

const YOU = 'you';

/** A real seed, not `Math.random`: the dice are the whole game. */
function freshSeed(): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0]!;
}

/** The engine mutates; React needs a new object. State is plain JSON by design. */
function step(g: GameState, fn: (draft: GameState) => void): GameState {
  const draft = JSON.parse(JSON.stringify(g)) as GameState;
  fn(draft);
  return draft;
}

interface Setup {
  bots: number;
  difficulty: Difficulty;
  rules: Rules;
}

export function SoloGame({ onExit, name }: { onExit: () => void; name: string }) {
  const [setup, setSetup] = useState<Setup>({
    bots: 2,
    difficulty: 'steady',
    rules: { target: 10_000, threshold: 500, finalRound: true, piggyback: true },
  });
  const [game, setGame] = useState<GameState | null>(null);

  const start = () => {
    setGame(
      newGame(
        [
          { id: YOU, name: name.trim() || 'You', isBot: false },
          ...Array.from({ length: setup.bots }, (_, i) => ({
            id: `bot${i + 1}`,
            name: BOT_NAMES[i % BOT_NAMES.length]!,
            isBot: true,
          })),
        ],
        freshSeed(),
        setup.rules,
      ),
    );
  };

  if (!game) return <SoloSetup setup={setup} onChange={setSetup} onStart={start} onExit={onExit} />;
  return (
    <SoloTable
      game={game}
      // Narrowed here rather than in the child: the table always has a game, and
      // making it prove that on every update would be noise in the hot path.
      setGame={(fn) => setGame((g) => (g ? fn(g) : g))}
      difficulty={setup.difficulty}
      onQuit={() => setGame(null)}
      onExit={onExit}
    />
  );
}

// ── setup ───────────────────────────────────────────────────────────────────

function SoloSetup({
  setup,
  onChange,
  onStart,
  onExit,
}: {
  setup: Setup;
  onChange: (s: Setup) => void;
  onStart: () => void;
  onExit: () => void;
}) {
  const set = (patch: Partial<Setup>) => onChange({ ...setup, ...patch });
  const setRules = (patch: Partial<Rules>) => onChange({ ...setup, rules: { ...setup.rules, ...patch } });

  return (
    <Page>
      <Masthead sub="you versus the house" />

      <Field label="How many opponents">
        <div className="opt-row">
          {[1, 2, 3, 4, 5].map((n) => (
            <Opt key={n} on={setup.bots === n} onClick={() => set({ bots: n })} label={`${n} opponents`}>
              {n}
            </Opt>
          ))}
        </div>
      </Field>

      <Field label="How well they play">
        <div className="opt-row">
          {(
            [
              ['easy', 'Loose'],
              ['steady', 'Steady'],
              ['sharp', 'Sharp'],
            ] as const
          ).map(([key, label]) => (
            <Opt key={key} wide on={setup.difficulty === key} onClick={() => set({ difficulty: key })}>
              {label}
            </Opt>
          ))}
        </div>
      </Field>

      <Field label="Play to">
        <div className="opt-row">
          {[2000, 5000, 10_000, 20_000].map((t) => (
            <Opt key={t} wide on={setup.rules.target === t} onClick={() => setRules({ target: t })}>
              {fmt(t)}
            </Opt>
          ))}
        </div>
      </Field>

      <Field label="Points needed to get on the board">
        <div className="opt-row">
          {[0, 350, 500, 1000].map((t) => (
            <Opt key={t} wide on={setup.rules.threshold === t} onClick={() => setRules({ threshold: t })}>
              {t === 0 ? 'None' : t}
            </Opt>
          ))}
        </div>
      </Field>

      <Check on={setup.rules.finalRound} onClick={() => setRules({ finalRound: !setup.rules.finalRound })}>
        Everyone else gets one last turn once someone hits the target
      </Check>

      <Check on={setup.rules.piggyback} onClick={() => setRules({ piggyback: !setup.rules.piggyback })}>
        Piggyback — if the player before you stops with dice left, you can throw their dice and start on their score
      </Check>

      <div className="spacer" />
      <div className="btn-stack">
        <button type="button" className="btn btn--primary" onClick={onStart}>
          DEAL ME IN
        </button>
        <button type="button" className="btn btn--ghost" onClick={onExit}>
          Back
        </button>
      </div>
    </Page>
  );
}

// ── the table ───────────────────────────────────────────────────────────────

function SoloTable({
  game,
  setGame,
  difficulty,
  onQuit,
  onExit,
}: {
  game: GameState;
  setGame: (fn: (g: GameState) => GameState) => void;
  difficulty: Difficulty;
  onQuit: () => void;
  onExit: () => void;
}) {
  const view = viewFor(game);
  const onTurn = game.players[game.turn]!;
  const botsTurn = onTurn.isBot;

  /**
   * A ref, not state, and checked inside the timeout: React 18 StrictMode runs
   * effects twice in development, and without this the second pass schedules a
   * duplicate bot action that lands one throw later on a state it was never
   * composed against.
   */
  const pending = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (pending.current !== null) window.clearTimeout(pending.current);
    };
  }, []);

  const apply = useCallback(
    (fn: (draft: GameState) => void) => {
      setGame((g) =>
        step(g, (draft) => {
          try {
            fn(draft);
          } catch {
            // A rejected move means the tap raced the state. The next render is
            // the truth; there is nothing for the player to fix.
          }
        }),
      );
    },
    [setGame],
  );

  // The FARKLE beat. The engine has no timers, so the stamp stays up exactly as
  // long as this component says and not one frame longer.
  useEffect(() => {
    if (game.phase !== 'farkle') return;
    const id = window.setTimeout(() => apply((d) => endTurn(d, d.players[d.turn]!.id)), FARKLE_BEAT_MS);
    return () => window.clearTimeout(id);
  }, [game.phase, game.throwId, game.turn, apply]);

  // The opponents. One action per tick, paced by what it is worth watching.
  useEffect(() => {
    if (!botsTurn || game.phase === 'over' || game.phase === 'farkle') return;
    const move = botMove(view, difficulty);
    if (!move) return;
    const delay = move.t === 'toggle' ? BOT_PICK_MS : BOT_THINK_MS;
    const id = window.setTimeout(() => {
      apply((d) => {
        const me = d.players[d.turn]!;
        if (move.t === 'roll') roll(d, me.id);
        else if (move.t === 'takeCarry') takeCarry(d, me.id);
        else if (move.t === 'toggle') toggle(d, me.id, move.index);
        else if (move.t === 'bank') bank(d, me.id);
        else endTurn(d, me.id);
      });
    }, delay);
    pending.current = id;
    return () => window.clearTimeout(id);
  }, [botsTurn, game.phase, game.throwId, game.turn, game.dice, difficulty, apply, view]);

  if (game.phase === 'over') {
    return <SoloOver game={game} onAgain={onQuit} onExit={onExit} />;
  }

  return (
    <Page>
      <Table
        view={view}
        youId={YOU}
        onRoll={() => apply((d) => roll(d, YOU))}
        onToggle={(i) => apply((d) => toggle(d, YOU, i))}
        onClear={() => apply((d) => clearHeld(d, YOU))}
        onBank={() => apply((d) => bank(d, YOU))}
        onTakeCarry={() => apply((d) => takeCarry(d, YOU))}
        action={{ label: 'Quit', onClick: onQuit }}
      />
    </Page>
  );
}

function SoloOver({ game, onAgain, onExit }: { game: GameState; onAgain: () => void; onExit: () => void }) {
  const rankedList = [...game.players].sort((a, b) => b.score - a.score);
  const top = rankedList[0]?.score ?? 0;
  const winners = rankedList.filter((p) => p.score === top);
  const youWon = winners.some((p) => p.id === YOU);

  return (
    <Page>
      <div className="rise">
        <Masthead sub={youWon ? 'you took it' : 'final tally'} />

        <div className="card center winner">
          <Eyebrow tone="blood">{winners.length > 1 ? 'Tied at the top' : 'Winner'}</Eyebrow>
          <div className="winner-name">{winners.map((w) => (w.id === YOU ? 'You' : w.name)).join(' & ')}</div>
          <div className="num winner-score">{fmt(top)}</div>
        </div>

        <div className="standings final-standings">
          {rankedList.map((p, i) => (
            <div className="row" key={p.id}>
              <span className="rank">{i + 1}</span>
              <span className="who">{p.id === YOU ? 'You' : p.name}</span>
              <span className="score">{fmt(p.score)}</span>
            </div>
          ))}
        </div>

        <div className="btn-stack">
          <button type="button" className="btn btn--primary" onClick={onAgain}>
            PLAY AGAIN
          </button>
          <button type="button" className="btn btn--ghost" onClick={onExit}>
            Back to the menu
          </button>
        </div>
      </div>
    </Page>
  );
}
